/**
 * ContactsPage — People.
 *
 * Residence Journal · a quiet directory of the people who help the
 * household run. Not a contact database — a family address book.
 *
 * Every person feels like someone important, categorised the way a
 * family actually thinks: Family · School · Medical · Trades · Friends
 * · Community · Emergency.
 *
 * Presentation only. No new features, no logic changes. Growing home:
 * empty by default with an invitation to add the first person.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import BetaFeedbackButton from "../../components/BetaFeedbackButton";
import { useHomeOSState } from "./state/HomeOSStateProvider";
import { usePersistentState } from "./state/usePersistentState";
import {
  ResidenceLayout, PageHeading, EditorialSection,
  TextLink, PillButton, Colophon, useLightPhase,
} from "./components/os/editorial";
import { ResidenceRail } from "./components/os/ResidenceRail";

const CATEGORIES = [
  { id: "family",    label: "Family",    subtitle: "Extended family and close relatives." },
  { id: "school",    label: "School",    subtitle: "Teachers, coordinators and other parents." },
  { id: "medical",   label: "Medical",   subtitle: "Doctors, dentists, specialists." },
  { id: "trades",    label: "Trades",    subtitle: "Plumbers, electricians, gardeners." },
  { id: "friends",   label: "Friends",   subtitle: "The friendships you share meals with." },
  { id: "community", label: "Community", subtitle: "Neighbours, clubs, everyday helpers." },
  { id: "emergency", label: "Emergency", subtitle: "The people you'd call in a hurry." },
];

export default function ContactsPage() {
  const { phase } = useLightPhase();
  useHomeOSState(); // Kept for parity with prior imports.
  const [people, setPeople] = usePersistentState("people.contacts", []);
  const [editing, setEditing] = useState(null);
  const [openCategory, setOpenCategory] = useState(null);

  const draftEmpty = { id: "", name: "", category: "family", role: "", phone: "", email: "", note: "" };

  const byCategory = useMemo(() => {
    const map = {};
    CATEGORIES.forEach(c => { map[c.id] = []; });
    people.forEach(p => {
      const key = CATEGORIES.find(c => c.id === (p.category || '').toLowerCase()) ? p.category : 'family';
      map[key] = map[key] || [];
      map[key].push(p);
    });
    return map;
  }, [people]);

  const focal = people.length === 0
    ? "The people who help this household run — add the first."
    : people.length === 1
      ? "One person in the family address book."
      : `${people.length} people in the family address book.`;

  const openAdd = (categoryId = "family") => {
    setEditing({ ...draftEmpty, id: `p_${Date.now()}`, category: categoryId, mode: "add" });
  };
  const openEdit = (person) => {
    setEditing({ ...person, mode: "edit" });
  };
  const closeEdit = () => setEditing(null);

  const savePerson = () => {
    if (!editing) return;
    if (!editing.name.trim()) { toast.error("Give this person a name."); return; }
    const clean = {
      id: editing.id,
      name: editing.name.trim(),
      category: editing.category,
      role: editing.role.trim(),
      phone: editing.phone.trim(),
      email: editing.email.trim(),
      note: editing.note.trim(),
    };
    setPeople(list => {
      const existing = list.findIndex(p => p.id === clean.id);
      if (existing >= 0) {
        const copy = [...list]; copy[existing] = clean; return copy;
      }
      return [clean, ...list];
    });
    toast.success(editing.mode === "add" ? `${clean.name} added.` : `${clean.name} updated.`);
    closeEdit();
  };

  const removePerson = (id) => {
    if (typeof window !== "undefined" && !window.confirm("Remove this person from the address book?")) return;
    setPeople(list => list.filter(p => p.id !== id));
    toast("Removed.");
  };

  const openCat = openCategory ? CATEGORIES.find(c => c.id === openCategory) : null;
  const openCatPeople = openCat ? (byCategory[openCat.id] || []) : [];

  return (
    <ResidenceLayout rail={<ResidenceRail currentKey="family" />} phase={phase} testid="module-page-contacts">
      <PageHeading
        testid="contacts-hero"
        module="family"
        room="People"
        title="People"
        focal={focal}
        actions={
          <>
            <PillButton variant="solid" onClick={() => openAdd("family")} testid="contacts-add-person-btn">
              Add a person
            </PillButton>
            <TextLink onClick={() => window.location.href = "/home-os/family"} testid="contacts-view-family">
              View The Family Room
            </TextLink>
          </>
        }
      />

      <div className="hidden" data-testid="module-title" aria-hidden>People</div>
      <div className="hidden" data-testid="module-subtitle" aria-hidden>Names, roles and how we help one another.</div>

      {/* ── Categories or category detail ─────────────────────────── */}
      <EditorialSection
        testid="contacts-categories"
        moduleMark={{ module: 'family', label: openCat ? openCat.label : 'Address book' }}
        title={openCat ? openCat.label : "How we know people"}
        aside={
          openCat ? (
            <TextLink onClick={() => setOpenCategory(null)} testid="contacts-back">
              ← All categories
            </TextLink>
          ) : (
            <TextLink onClick={() => openAdd("family")} testid="contacts-add-inline">
              Add a person
            </TextLink>
          )
        }
      >
        {!openCat ? (
          <ul>
            {CATEGORIES.map((cat, i, arr) => {
              const count = (byCategory[cat.id] || []).length;
              return (
                <li
                  key={cat.id}
                  className={`${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                  style={{ borderColor: 'var(--ara-line-soft)' }}
                >
                  <button
                    type="button"
                    onClick={() => setOpenCategory(cat.id)}
                    className="w-full grid grid-cols-[1fr_auto_auto] items-baseline gap-6 py-4 text-left transition-ara hover:opacity-70"
                    data-testid={`contacts-cat-${cat.id}`}
                  >
                    <div className="min-w-0">
                      <p
                        className="text-[15.5px] leading-tight"
                        style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
                      >
                        {cat.label}
                      </p>
                      <p
                        className="mt-1 text-[13px]"
                        style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-muted)', fontWeight: 400 }}
                      >
                        {cat.subtitle}
                      </p>
                    </div>
                    <p
                      className="text-[13px] tabular-nums whitespace-nowrap"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-soft)', fontWeight: 400 }}
                      data-testid={`contacts-cat-count-${cat.id}`}
                    >
                      {count} {count === 1 ? 'person' : 'people'}
                    </p>
                    <span
                      className="text-[13px]"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-soft)' }}
                      aria-hidden
                    >
                      →
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : openCatPeople.length === 0 ? (
          <div>
            <p
              className="italic text-[17px] sm:text-[19px] leading-[1.4]"
              style={{
                fontFamily: "var(--ara-font-display), 'Times New Roman', serif",
                color: 'var(--ara-ink)', fontWeight: 400, maxWidth: '32ch',
              }}
              data-testid={`contacts-empty-${openCat.id}`}
            >
              No {openCat.label.toLowerCase()} added yet. Add the first.
            </p>
            <div className="mt-6">
              <PillButton
                variant="solid"
                onClick={() => openAdd(openCat.id)}
                testid={`contacts-add-first-${openCat.id}`}
              >
                Add someone
              </PillButton>
            </div>
          </div>
        ) : (
          <ul data-testid={`contacts-list-${openCat.id}`}>
            {openCatPeople.map((p, i, arr) => (
              <li
                key={p.id}
                className={`grid grid-cols-[1fr_auto] items-baseline gap-4 py-4 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                style={{ borderColor: 'var(--ara-line-soft)' }}
                data-testid={`contacts-person-${p.id}`}
              >
                <div className="min-w-0">
                  <p
                    className="text-[15px] leading-tight"
                    style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
                  >
                    {p.name}
                  </p>
                  <p
                    className="mt-1 text-[13px]"
                    style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-muted)', fontWeight: 400 }}
                  >
                    {[p.role, p.phone, p.email].filter(Boolean).join(' · ') || '—'}
                  </p>
                  {p.note && (
                    <p
                      className="mt-1 italic text-[12.5px]"
                      style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink-soft)', fontWeight: 400 }}
                    >
                      {p.note}
                    </p>
                  )}
                </div>
                <div className="flex items-baseline gap-x-4 whitespace-nowrap">
                  <TextLink onClick={() => openEdit(p)} testid={`contacts-edit-${p.id}`}>Edit</TextLink>
                  <TextLink onClick={() => removePerson(p.id)} testid={`contacts-remove-${p.id}`}>Remove</TextLink>
                </div>
              </li>
            ))}
          </ul>
        )}
      </EditorialSection>

      {/* ── How Ara helps ─────────────────────────────────────────── */}
      <EditorialSection
        testid="contacts-ara-helps"
        moduleMark={{ module: 'family', label: 'Ara' }}
        title="How Ara helps"
      >
        <p
          className="italic text-[16px] leading-[1.5]"
          style={{
            fontFamily: "var(--ara-font-display), 'Times New Roman', serif",
            color: 'var(--ara-ink)', fontWeight: 400, maxWidth: '48ch',
          }}
        >
          Ara can spot new contacts in the weekly reset — teachers, tradespeople, coaches — and quietly add them here for you.
        </p>
      </EditorialSection>

      <Colophon>ARA · People</Colophon>

      {/* ── Person edit dialog ────────────────────────────────────── */}
      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: 'rgba(31, 31, 31, 0.5)' }}
          onClick={closeEdit}
          data-testid="contacts-edit-dialog"
        >
          <div
            className="w-full max-w-md rounded-[20px] p-6 sm:p-8"
            style={{ background: 'var(--ara-surface)', border: '1px solid var(--ara-line)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="caption-ara mb-2">{editing.mode === "add" ? "New person" : "Edit person"}</p>
            <h3
              className="text-[24px] leading-tight tracking-[-0.01em]"
              style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink)', fontWeight: 400 }}
            >
              {editing.mode === "add" ? "Add someone" : editing.name || "Edit"}
            </h3>

            <div className="mt-6 space-y-4">
              <PersonField label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="contacts-field-name" />
              <PersonSelect label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} options={CATEGORIES} testid="contacts-field-category" />
              <PersonField label="Role" value={editing.role} onChange={(v) => setEditing({ ...editing, role: v })} testid="contacts-field-role" placeholder="e.g. Family GP" />
              <PersonField label="Phone" value={editing.phone} onChange={(v) => setEditing({ ...editing, phone: v })} testid="contacts-field-phone" />
              <PersonField label="Email" value={editing.email} onChange={(v) => setEditing({ ...editing, email: v })} testid="contacts-field-email" />
              <PersonField label="Note" value={editing.note} onChange={(v) => setEditing({ ...editing, note: v })} testid="contacts-field-note" placeholder="Anything worth remembering" />
            </div>

            <div className="mt-7 flex items-center justify-end gap-6">
              <button
                type="button"
                onClick={closeEdit}
                className="link-ara"
                data-testid="contacts-edit-cancel"
              >
                Cancel
              </button>
              <PillButton variant="solid" onClick={savePerson} testid="contacts-edit-save">
                Save
              </PillButton>
            </div>
          </div>
        </div>
      )}

      <BetaFeedbackButton />
    </ResidenceLayout>
  );
}

function PersonField({ label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="caption-ara block mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent outline-none py-2 text-[14px]"
        style={{
          fontFamily: 'Inter, system-ui, sans-serif',
          color: 'var(--ara-ink)',
          borderBottom: '1px solid var(--ara-line)',
        }}
        data-testid={testid}
      />
    </div>
  );
}
function PersonSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="caption-ara block mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent outline-none py-2 text-[14px]"
        style={{
          fontFamily: 'Inter, system-ui, sans-serif',
          color: 'var(--ara-ink)',
          borderBottom: '1px solid var(--ara-line)',
        }}
        data-testid={testid}
      >
        {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </div>
  );
}
